import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';
import { getStorageItem } from 'utils/storage';

import { FlowKey } from './shared/types';
import { getFlowChoice, resolveFlowEligibility, resolveRedirect } from './shared/eligibility';
import Button from 'components/ui/button';

import { FlowLoading, FlowShell } from './shared/FlowLoading';

// Guarda-chuva da apreciação de Realização Destaque (ELE-02): resolve a elegibilidade do
// gestor logado e redireciona para o fluxo correspondente — /diretoria-rede (Normal) ou
// /mad. Elegível a ambos respeita a última escolha; a nenhum, exibe a mensagem de acesso
// negado. O corpo monolítico anterior foi separado em normal/ (FluxoNormal) e mad/
// (FluxoMAD); o histórico permanece no git.
//
// Estados (ELE-02): 'loading' enquanto a elegibilidade não resolve (FlowLoading — o mesmo
// shell que os fluxos mostram, então gate → fluxo é um loading contínuo, sem "pisca");
// 'error' quando a chamada falha (rede/servidor) — distinto de 'no-access', que é a
// elegibilidade resolvida SEM nenhum fluxo. O erro é recuperável ("Tentar novamente").
type GateState = 'loading' | 'error' | 'no-access' | FlowKey;

export function ApreciacaoFlowGate() {
  const { user, cycleSelected } = useAuth();
  const [state, setState] = useState<GateState>('loading');
  // Incrementado pelo botão "Tentar novamente" para re-disparar a resolução após erro.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function decide() {
      if (!user?._id) return;

      setState('loading');

      // Lateralidade/substituição: a home grava manager_lateralidade antes de navegar
      // para cá — o gate roteia pela elegibilidade do gestor SUBSTITUÍDO (mesma regra
      // dos fluxos, que resolvem internalUser pela mesma chave de storage).
      const lateralId = getStorageItem('manager_lateralidade');
      const playerId = typeof lateralId === 'string' && lateralId.length ? lateralId : user._id;

      // A elegibilidade é por CICLO, e o ciclo em vigor é o que o gestor deixou
      // selecionado (o AuthContext o mantém entre as telas). Ainda indefinido, o backend
      // responde pelo ciclo corrente — e a página de destino refaz a conta assim que o
      // ciclo resolve, desviando de fluxo se for o caso (useFlowEligibility).
      const eligibility = await resolveFlowEligibility(playerId, { cycleId: cycleSelected?._id });

      if (cancelled) return;

      // null = não foi possível determinar a elegibilidade (falha de rede/servidor) —
      // NÃO é "sem acesso". Sem acesso é a elegibilidade resolvida sem nenhum fluxo.
      if (!eligibility) {
        setState('error');
        return;
      }

      const target = resolveRedirect(eligibility, getFlowChoice());
      setState(target ?? 'no-access');
    }

    void decide();

    return () => {
      cancelled = true;
    };
  }, [user?._id, cycleSelected?._id, reloadToken]);

  if (state === 'loading') {
    return <FlowLoading />;
  }

  if (state === 'error') {
    return (
      <FlowShell>
        <div className="rounded-jj-panel bg-brand-white p-8 text-center shadow-jj-01">
          <p className="m-0 mb-2 font-display text-lg font-bold text-brand-blue-dark">
            Não foi possível carregar suas realizações.
          </p>
          <p className="m-0 mb-6 text-sm text-content">Verifique sua conexão e tente novamente.</p>
          <Button variant="solido-destaque" onClick={() => setReloadToken(token => token + 1)}>
            Tentar novamente
          </Button>
        </div>
      </FlowShell>
    );
  }

  if (state === 'no-access') {
    return (
      <FlowShell>
        <div className="rounded-jj-panel bg-brand-white p-8 text-center shadow-jj-01">
          <p className="m-0 font-display text-lg font-bold text-brand-blue-dark">Você não possui acesso à esta página.</p>
        </div>
      </FlowShell>
    );
  }

  return <Navigate to={state} replace />;
}
